/**
 * @file the GrowthModule’s purpose is to facilitate state changes to the AdminGrowthData
 * and OrgGrowthData object. This typically includes Growth experiments and Onboarding.
 * If your changes/additions to the GrowthModule do not require updates to the
 * AdminGrowthData or OrgGrowthData object and are not directly related to an active
 * experiment or Onboarding then please consider another module or create a new one.
 */
import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { merge } from 'lodash';
import OrganizationsApi from 'api/v2/OrganizationsApi';
import Vue from 'vue';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

// --------------------------------------------------------------------------
// Private functions
// --------------------------------------------------------------------------
const putOrgGrowthData = (orgId, growthData) => OrganizationsApi
  .updateOrganizationSettings(orgId, { settings: { growthData } });

// --------------------------------------------------------------------------
// Actions
// --------------------------------------------------------------------------

function setOrgGrowthData({ commit, rootState, state }, { growthData, pushChanges = true }) {
  commit('SET_ORG_GROWTH_DATA', { growthData });

  if (pushChanges) {
    const { orgGrowthData } = state;
    const { orgId } = rootState[settingsModule];
    putOrgGrowthData(orgId, orgGrowthData);
  }
}

function updateExperimentStates({ commit, rootState, state }, experiments) {
  const { orgGrowthData } = state;
  const { orgId } = rootState[settingsModule];

  commit('UPDATE_EXPERIMENT_STATES', experiments);
  putOrgGrowthData(orgId, orgGrowthData);
}

function updateIsMobile({ commit }, isMobile) {
  commit('SET_IS_MOBILE', isMobile);
}

// --------------------------------------------------------------------------
// Getters
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

function SET_IS_MOBILE(state, isMobile) {
  Vue.set(state, 'isMobile', isMobile);
}

/**
 * Sets the org growthData object to the state
 * @param {Object} growthData - the org growthData object
 */
function SET_ORG_GROWTH_DATA(state, { growthData, maintainPreviousObject = true }) {
  if (maintainPreviousObject) {
    const newOrgGrowthObject = merge({}, state.orgGrowthData, growthData);
    Vue.set(state, 'orgGrowthData', newOrgGrowthObject);
  } else {
    Vue.set(state, 'orgGrowthData', growthData);
  }
}

function UPDATE_EXPERIMENT_STATES(state, experiments) {
  Vue.set(state.orgGrowthData, 'experimentStates', experiments);
}

// this is exported so we can create new modules for unit testing
export const createGrowthModule = () => ({
  namespaced: true,
  state: {
    isMobile: false,
    optimizelyLoaded: false,

    // org level growthData to track experiments
    orgGrowthData: {
      experimentStates: {},
    },
  },
  actions: {
    setOrgGrowthData,
    updateExperimentStates,
    updateIsMobile,
  },
  getters: {},
  mutations: {
    SET_IS_MOBILE,
    SET_ORG_GROWTH_DATA,
    UPDATE_EXPERIMENT_STATES,
  },
});

export default createGrowthModule();
